import {useRef} from 'react'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import useBodyScrollLock from '@vio/atlas/src/components/designSystem/Overlay/useBodyScrollLock'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

const Wrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s500};
  ${cssTheme.typography.bodyM}
  .--section {
    &:not(:last-of-type) {
      padding-bottom: ${cssTheme.layout.spacing.s600};
    }
    .--title {
      margin-bottom: ${cssTheme.layout.spacing.s300};
    }
    ul {
      padding-top: 0;
      margin-top: 0;
      padding-left: ${cssTheme.layout.spacing.s700};
    }
  }

  ${linariaMq.desktopXs} {
    padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s700};
  }
`

const Title = ({children}: {children: React.ReactNode}) => (
  <Text className="--title" variant="titleL">
    {children}
  </Text>
)

const Section = ({children}: {children: React.ReactNode}) => (
  <Box className="--section">{children}</Box>
)

interface HowSearchWorksContentProps {
  isOpen?: boolean
}

export const HowSearchWorksContent = ({
  isOpen = false
}: HowSearchWorksContentProps) => {
  const ref = useRef<HTMLDivElement>(null)
  useBodyScrollLock({ref, isEnabled: isOpen})

  return (
    <Wrapper ref={ref} data-body-scroll-lock-ignore>
      <Section>
        <Title>
          <FormattedMessageWrapper
            id="howSearchWorks.searchTitle"
            defaultMessage="How search works"
          />
        </Title>
        <FormattedMessageWrapper
          id="howSearchWorks.searchContent"
          defaultMessage="Our goal is to help travelers plan and book their stays with confidence.<br></br><br></br>We work with a wide range of trusted travel providers to show you a range of accommodation options and prices."
          values={{
            br: () => <br />
          }}
        />
      </Section>

      <Section>
        <Title>
          <FormattedMessageWrapper
            id="howSearchWorks.rankHotelsTitle"
            defaultMessage="How we rank hotels"
          />
        </Title>
        <FormattedMessageWrapper
          id="howSearchWorks.rankHotelsContent"
          defaultMessage='Important: Our rankings take into account payments (such as commissions) we receive from travel providers when users click through or book via our platform. This affects which offers are shown and the order in which they appear.<br></br><br></br>When you search, our system ranks results based on several factors designed to highlight the most relevant and highest-quality options. These include:<ul><li>Price and value for money – competitive deals and offers.</li><li>Hotel popularity – how often the property is booked and rated by travelers.</li><li>Location quality – how close is the property to attractions and amenities.</li><li>Guest reviews – ratings and feedback from other users.</li><li>Availability and deal quality – offers with strong availability or better terms may appear higher.</li><li>Payments we receive from providers - commissions or other fees paid to us by travel providers, which affect ranking.</li></ul>Our aim is to show you the best value stays, not just the cheapest ones. "Best value" or "Recommended" results are not necessarily the cheapest options available.'
          values={{
            br: () => <br />,
            ul: chunk => <ul>{chunk}</ul>,
            li: chunk => <li>{chunk}</li>
          }}
        />
      </Section>

      <Section>
        <Title>
          <FormattedMessageWrapper
            id="howSearchWorks.rankOffersTitle"
            defaultMessage="How we rank offers within a hotel"
          />
        </Title>
        <FormattedMessageWrapper
          id="howSearchWorks.rankOffersContent"
          defaultMessage="When a hotel is available through multiple providers, you may see several offers for the same property. Their order depends on:<ul><li>If it's our deal or one offered by our partners</li><li>Total price (including taxes and fees where possible)</li><li>Flexibility (e.g., free cancellation or pay later options)</li><li>Competitiveness of the deal</li><li>Payments we receive from providers (such as commissions), which affect the order in which offers appear.</li></ul>Some providers pay us a commission when you book through us. This does influence the order in which their offers appear, but our priority is always to present transparent and trustworthy choices for travelers."
          values={{
            ul: chunk => <ul>{chunk}</ul>,
            li: chunk => <li>{chunk}</li>
          }}
        />
      </Section>

      <Section>
        <Title>
          <FormattedMessageWrapper
            id="howSearchWorks.transparencyTitle"
            defaultMessage="Transparency and coverage"
          />
        </Title>
        <FormattedMessageWrapper
          id="howSearchWorks.transparencyContent"
          defaultMessage="We aim to show clear, accurate prices and details for each offer. However, not every travel provider in the market is included.<br></br><br></br>We're continuously expanding our partnerships to bring you more choice, but some hotels or rates might not appear in our results yet."
          values={{
            br: () => <br />
          }}
        />
      </Section>

      <Section>
        <Title>
          <FormattedMessageWrapper
            id="howSearchWorks.refineResultsTitle"
            defaultMessage="How to refine your results"
          />
        </Title>
        <FormattedMessageWrapper
          id="howSearchWorks.refineResultsContent"
          defaultMessage="You can customize your search to fit your travel needs. Use filters and sorting tools to:<ul><li>Sort by price, guest rating, or distance</li><li>Filter by property type, amenities, or cancellation policy</li><li>Explore the map view to find stays in specific locations</li></ul>These tools help you control how results appear and focus on what matters most to you."
          values={{
            ul: chunk => <ul>{chunk}</ul>,
            li: chunk => <li>{chunk}</li>
          }}
        />
      </Section>

      <Section>
        <Title>
          <FormattedMessageWrapper
            id="howSearchWorks.makeMoneyTitle"
            defaultMessage="How we make money"
          />
        </Title>
        <FormattedMessageWrapper
          id="howSearchWorks.makeMoneyContent"
          defaultMessage="We earn revenue mainly through commissions paid by travel providers when you complete a booking through our site. We also earn revenue from ads, which may occasionally appear in your search results.<br></br><br></br>These commissions and payments are taken into account in how offers are ranked and displayed."
          values={{
            br: () => <br />
          }}
        />
      </Section>

      <Section>
        <Title>
          <FormattedMessageWrapper
            id="howSearchWorks.bottomLineTitle"
            defaultMessage="The bottom line"
          />
        </Title>
        <FormattedMessageWrapper
          id="howSearchWorks.bottomLineContent"
          defaultMessage="<ul><li>We help travelers find and book hotels easily and confidently.</li><li>Search results are based on price, popularity, reviews, and competitiveness.</li><li>Some providers pay us commissions and these payments affect which offers are shown and the order in which they appear.</li><li>You can always adjust filters and sort options to see what suits you best.</li><li>Transparency and fairness guide how our search works.</li></ul>"
          values={{
            ul: chunk => <ul>{chunk}</ul>,
            li: chunk => <li>{chunk}</li>
          }}
        />
      </Section>
    </Wrapper>
  )
}
